import BaseView, { DomainModel3D } from './base-view';
import CacheByModelValue from '../mem/cache-by-model-value';
import logging from '../logging';
import { Attr } from '../imw/indexed-mesh';
import { DIRECTION_EAST, DIRECTION_SOUTH, HALF_PI, HALF_TURN, QUARTER_TURN } from '../math/constants';
import { DiscArray } from '../imw/disc-array';
import { Fan } from '../imw/fan';
import { GridLayout } from '../imw/grid-layout';
import { HoleArray } from '../imw/hole-array';
import { IndexedMeshWriter } from '../imw/indexed-mesh-writer';
import { InvertedFan } from '../imw/inverted-fan';
import { Join } from '../imw/join';
import { Mesh, BufferGeometry, Material, Matrix4, MeshStandardMaterial } from 'three';
import { ReverseWinding } from '../imw/reverse-winding';
import { RigidTransformMesh } from '../imw/rigid-transform-mesh';
import { ScaleUV } from '../imw/scale-uv';
import { SweptMesh } from '../imw/swept-mesh';
import { Transform } from '../imw/transform';
import { Triangle } from '../imw/triangle';
import { VerticalPanel, Material as DomainMaterial } from '../domain';
import { generateBufferGeometry } from '../imw/generate-buffer-geometry';

import { VertexNormalsHelper } from 'three/examples/jsm/helpers/VertexNormalsHelper'
import { SetNormal } from '../imw/set-normal';
import { Vec2, VertexList } from '../imw/vertex-list';
import { VerticalPanelViewConfig, verticalPanelViewDefaults, VerticalPanelViewOptions } from './views-config';
import { update, clone } from "../json/optional-properties";
import { Quad } from '../imw/quad';
import { generateEdgebandProfile } from '../view3d/generate-edgeband-profile';

const log = logging('view');


const
    HOLE_RADIUS = 0.0984251968503937, // 2.5mm in inches
    HOLE_SETBACK = 1.4566929133858268, // 37mm in inches
    HOLE_ARRAY_WIDTH = 1.5551181102362206; // 42mm in inches


type PanelAttrs = {
    normal: Attr;
    uv: Attr;
};


export default class VerticalPanelView extends BaseView {

    declare model: VerticalPanel;

    mesh: Mesh;

    config: VerticalPanelViewConfig = verticalPanelViewDefaults;

    highlights: Set<Mesh> = new Set();

    // DEBUG
//    normals: VertexNormalsHelper;

    constructor(model: DomainModel3D, options: VerticalPanelViewOptions) {
        super(model);

        update(this.config, options);
        //this.config.standardHeights = clone(options.standardHeights);

        this.init();

        this.onReady.name = 'VerticalPanelView';
    }

    make() {
        this.userData.isParametric = true;
        this.name = 'VerticalPanel';

        this.mesh = new Mesh(new BufferGeometry(), new Material());
        this.mesh.castShadow = true;
        this.mesh.receiveShadow = true;
        this.add(this.mesh);

    }

    bind(): void {
        this.model.bind('width', this.changeSize);
        this.model.on('depth', this.changeSize);
        this.model.bind('thickness', this.changeSize);
        this.model.on('showLeftFaceHoles', this.changeSize);
        this.model.on('showRightFaceHoles', this.changeSize);

        //this.mesh.geometry = generateMesh(this.model, this.config.standardHeights);
        this.mesh.geometry = generateMesh(this.model);

        // DEBUG
//        if(true) {
//            this.normals = new VertexNormalsHelper(this.mesh, 1, 0x00FF80);
//            this.add(this.normals);
//        }

        this.bindMaterialArray(this.mesh, this.model.materials, ["face", "edge", "holes"]);
        this.model.bind('opacity', () => {
            const castShadow = this.model.opacity == 1;
            this.mesh.castShadow = castShadow;
        });
    }

    private changeSize = () => {
        this.updates.queue(
            //async () => generateMesh(this.model, this.config.standardHeights),
            async () => generateMesh(this.model),
            (geo: BufferGeometry) => {
                this.mesh.geometry = geo;
                this.onReady.trigger();
            }
        );
    }

}


/**
 *
 * Note that throughout the generateMesh method and its subroutines, much of
 * the math is done in the XY plane, which is then moved to the XZ plane. This
 * means that when working in XY, the "front" edge of the panel is toward the
 * +Y direction, i.e. on the right. It's as if we're looking at the panel from
 * its left face.
 */
const generateMesh = CacheByModelValue.memoize(
    [
        "thickness", "height", "depth", "showLeftFaceHoles", "showRightFaceHoles",
        "hutch", "hutchDepth", "hutchHeight", "hutchInnerRadius", "hutchOuterRadius",
        "topFrontCutbackDepth", "topFrontCutbackHeight", "bottomFrontRadius",
    ],
//    (model: VerticalPanel, heights: number[]) => {
    (model: VerticalPanel) => {


        // We compute some of the basic values needed throughout the procedure,
        // delegate most of the activity to sub-routines, and assemble the
        // results.
        const
            heights = generateHeights(model.height),
            // The edge banding is 1/16th of an inch thick
            offset = 0.0625,
            offset2 = 2*offset,

            depth = model.depth - offset2,
            height = model.height - offset2,
            invDepth = 1/depth,
            invHeight = 1/height,
            uvScale = Math.min(invDepth, invHeight),

            // the polygrid is the planar surface of the left and right sides
            // with voids at the corners and possibly at the hutch location.
            grid = new GridLayout(),
            plan = new VertexList(),
            profile = new VertexList(),

            // The extra meshes for the radius or cutback corners
            fillers: IndexedMeshWriter<PanelAttrs>[] = [];


        // Set the basic shape of the panel, which is a rectangle, minus the
        // thickness of the edge band
        grid.rect(offset, offset, depth, height);

        // Delegation to sub-routines
        generateEdgebandProfile(profile, model.thickness, offset);
        
        plan.moveTo(offset, offset);

        cutoutHutch(model, grid, offset);
        bottomFrontCorner(model, grid, plan, fillers, offset);
        hutchOuterCorner(model, grid, plan, fillers, offset);
        hutchInnerCorner(model, plan, fillers, offset);
        topFrontCorner(model, grid, plan, fillers, offset);

        // the subroutines above have left us in a state where we only have
        // to add the top back corner and 
        plan.edgeTo(offset, model.height - offset);
        plan.close();
        plan.first.inorm = new Vec2(-1, 0);
        plan.last.onorm = new Vec2(-1, 0);

        let
            leftFace: GridLayout,
            rightFace: GridLayout,
            leftFillers: IndexedMeshWriter<PanelAttrs>[],
            rightFillers: IndexedMeshWriter<PanelAttrs>[],
            // The discs that fill the holes and take the third material index
            leftDiscs: IndexedMeshWriter<PanelAttrs>[] = [],
            rightDiscs: IndexedMeshWriter<PanelAttrs>[] = [];

        if(model.showLeftFaceHoles && model.showRightFaceHoles) {
            // They're identical, so do the cutout before the cloning
            cutoutHoles(model, heights, grid, fillers, leftDiscs, offset);
            leftFace = grid;
            rightFace = grid.clone();
            leftFillers = rightFillers = fillers;
            rightDiscs = leftDiscs;
        } else if(model.showLeftFaceHoles) {
            // Only the left face gets holes, so clone and then cutout left
            leftFace = grid;
            rightFace = grid.clone();
            leftFillers = Array.from(fillers);
            rightFillers = fillers;
            cutoutHoles(model, heights, leftFace, leftFillers, leftDiscs, offset);
        }
        else if(model.showRightFaceHoles) {
            // Only the right face gets holes, so clone and then cutout right
            leftFace = grid;
            rightFace = grid.clone();
            leftFillers = fillers;
            rightFillers = Array.from(fillers);
            cutoutHoles(model, heights, rightFace, rightFillers, rightDiscs, offset);
        } else {
            leftFace = grid;
            rightFace = grid.clone();
            leftFillers = rightFillers = fillers;
        }

        const
            leftMat = new Matrix4(),
            rightMat = new Matrix4(),
            // Use a transform for the edge band to create a compound rotation
            edgeTransform = new Transform();

        // Both the left and right faces are built in the XY plane, which is
        // face-on to the camera when we're looking in the global -Z direction
        // (the default). We rotate them both 90 degrees clockwise so that they
        leftMat.makeRotationY(-QUARTER_TURN);
        rightMat.makeRotationY(-QUARTER_TURN);
        rightMat.setPosition(model.thickness, 0, 0);

        // The profile of the swept mesh stays in the XY plane and the plan
        // of the swept mesh is transferred into the XZ plane, i.e. the ground
        // plane in three.js. First we rotate it clockwise by 90 degrees on the
        // Z axis, which puts the entire edge band below the ground plane, but
        // it is now in the same plane as the transformed faces of the vertical
        // panel (the YZ plane). We rotate around the X axis, 90 degrees
        // clockwise (looking from +X to -X), which completes the alignment.
        edgeTransform.rotateX(QUARTER_TURN);
        edgeTransform.rotateY(-QUARTER_TURN);
        edgeTransform.translate(model.thickness, 0, 0);


        // Final assembly of the pieces. I'm using swizzling to do a 90 degree
        // rotation, but note that swizzling like this may cause the winding
        // order to flip whereas rotation would not cause the same flip.
        const
            left = new RigidTransformMesh(
                leftMat, new ScaleUV(
                    uvScale, uvScale,
                    new Join(leftFace, ...leftFillers))),
            right = new ReverseWinding(
                new SetNormal(1, 0, 0,
                    new RigidTransformMesh<PanelAttrs>(rightMat,
                       new ScaleUV(uvScale, uvScale,
                           new Join(rightFace, ...rightFillers))))),
            panelJoin = new Join(left, right),

            edgeBanding = new RigidTransformMesh(edgeTransform.m,
                new ScaleUV(uvScale, uvScale,
                    new SweptMesh(profile, plan))),

            // if we don't have any discs, we default to a degenerate Quad so
            // that we avoid the (presumed) bug in Join that mangles the mesh
            // when the last argument to join is empty.
            discs = leftDiscs.length == 0 && rightDiscs.length == 0
                ? new Quad(0, 0, 0, 0)
                : new Join(
                new RigidTransformMesh(leftMat,
                    new SetNormal(0, 0, -1,
                        new ScaleUV(uvScale, uvScale,
                            new Join(...leftDiscs)))),
                new ReverseWinding(
                    new RigidTransformMesh(rightMat,
                        new ScaleUV(uvScale, uvScale,
                            new Join(...rightDiscs))))),

            join = new Join(panelJoin, edgeBanding, discs);


        const geo = generateBufferGeometry(join);
        //geo.computeVertexNormals();

        for(const group of join.groups) {
            geo.addGroup(group.start, group.count, group.materialIndex);
        }

        return geo;
    }
);

const cutoutHutch = (
    model: VerticalPanel, grid: GridLayout, offset: number) =>
{
    if(! model.hutch) return;
    const
        offset2 = 2*offset,
        x = model.depth - model.hutchDepth - offset,
        y = model.hutchHeight - offset, 
        width = model.hutchDepth, // + offset2,
        height = model.height - model.hutchHeight + offset2;

    grid.void(x, y, width, height);
}

const topFrontCorner = (
    model: VerticalPanel, grid: GridLayout, plan: VertexList,
    corners: IndexedMeshWriter<PanelAttrs>[], offset: number) =>
{
    if(model.topFrontCutbackDepth > 0 && model.topFrontCutbackHeight > 0) {
        const
            height = model.topFrontCutbackHeight - offset,
            depth = model.topFrontCutbackDepth - offset,
            y = model.height - model.topFrontCutbackHeight;
        let x = model.depth - model.topFrontCutbackDepth;
        if(model.hutch) {
            x = x - model.hutchDepth;
        }
        grid.void(x, y, depth, height);
        corners.push(new Triangle(x, y, x+depth, y, x, y+height));
        plan.edgeTo(x+depth, y);
        plan.edgeTo(x, y+height);
    } else {
        const hutch = model.hutch ? model.hutchDepth : 0;
        plan.edgeTo(model.depth - hutch - offset , model.height - offset);
    }
}

const bottomFrontCorner = (
    model: VerticalPanel, grid: GridLayout, plan: VertexList,
    corners: IndexedMeshWriter<PanelAttrs>[], offset: number) =>
{
    if(model.bottomFrontRadius > 0) {
        // (x, y) is in the lower left corner
        const
            radius = model.bottomFrontRadius - offset,
            x = model.depth - model.bottomFrontRadius,
            y = offset,
            top = y + radius;
        grid.void(x, y, radius, radius);
        // the fan gets 13 vertices which means 12 segments
        corners.push(new Fan(
            x, top, radius, DIRECTION_SOUTH, DIRECTION_SOUTH + QUARTER_TURN, 13));
        plan.edgeTo(x, y);
        // the arc gets 12 segments
        plan.arc(DIRECTION_SOUTH, QUARTER_TURN, radius, 12);
    } else {
        plan.edgeTo(model.depth - offset, offset);
    }
}

const hutchOuterCorner = (
    model: VerticalPanel, grid: GridLayout, plan: VertexList,
    corners: IndexedMeshWriter<PanelAttrs>[], offset: number) =>
{
    if(model.hutch && model.hutchOuterRadius > 0) {
        const
            radius = model.hutchOuterRadius - offset,
            x = model.depth - model.hutchOuterRadius,
            y = model.hutchHeight - model.hutchOuterRadius;
        grid.void(x, y, radius, radius);
        // the fan gets 13 vertices which means 12 segments
        corners.push(new Fan(
            x, y, radius, DIRECTION_EAST, DIRECTION_EAST + QUARTER_TURN, 13));

        plan.edgeTo(x + radius, y);
        // the arc gets 12 segments
        plan.arc(DIRECTION_EAST, QUARTER_TURN, radius, 12);
    } else if(model.hutch) {
        plan.edgeTo(model.depth - offset, model.hutchHeight - offset);
    }
}

const hutchInnerCorner = (
    model: VerticalPanel, plan: VertexList,
    corners: IndexedMeshWriter<PanelAttrs>[], offset: number) =>
{
    if(model.hutch && model.hutchInnerRadius > 0) {
        const
            radius = model.hutchInnerRadius - offset,
            x = model.depth - model.hutchDepth - offset,
            y = model.hutchHeight - offset;
        // we don't need a void b/c we're filling a concave corner in the
        // hutch, i.e. the void for the hutch already provides space for the
        // InvArcMesh. The fan gets 13 vertices which means 12 segments
        corners.push(new InvertedFan(x, y, radius, 1, 13));

        plan.edgeTo(x + radius, y);
        // the arc gets 12 segments
        plan.arc(DIRECTION_SOUTH, -QUARTER_TURN, radius, 12);
    } else if(model.hutch) {
        plan.edgeTo(model.depth - model.hutchDepth - offset, model.hutchHeight - offset);
    }
}

const cutoutHoles = (
    model: VerticalPanel, heights: number[], grid: GridLayout,
    fillers: IndexedMeshWriter<PanelAttrs>[], discs: IndexedMeshWriter<PanelAttrs>[],
    offset: number) =>
{
    // Back edge is never interrupted
    {
        const
            x = offset,
            y = offset,
            width = HOLE_ARRAY_WIDTH - offset,
            height = model.height - 2*offset,
            cx = HOLE_SETBACK,
            radius = HOLE_RADIUS;
        grid.void(x, y, width, height);
        fillers.push(new HoleArray(x, y, width, height, cx, heights, radius, 8));
        discs.push(new DiscArray(cx, y, height, heights, radius, 8));
    }

    // The front edge may be in two parts
    if(model.hutch) {
        // Lower part of front edge, below the hutch
        {
            const
                x = model.depth - HOLE_ARRAY_WIDTH,
                y = Math.max(offset, model.bottomFrontRadius),
                width = HOLE_ARRAY_WIDTH - offset,
                height = model.hutchHeight - y - (model.hutchOuterRadius || offset),
                cx = model.depth - HOLE_SETBACK,
                radius = HOLE_RADIUS;
            grid.void(x, y, width, height);
            fillers.push(new HoleArray(x, y, width, height, cx, heights, radius, 8));
            discs.push(new DiscArray(cx, y, height, heights, radius, 8));
        }
        // Upper part of front edge, above the hutch
        {
            const
                x = model.depth - model.hutchDepth - HOLE_ARRAY_WIDTH,
                y = model.hutchHeight,
                width = HOLE_ARRAY_WIDTH - offset,
                height = model.height - y - (model.topFrontCutbackHeight || offset),
                cx = model.depth - model.hutchDepth - HOLE_SETBACK,
                radius = HOLE_RADIUS;
            grid.void(x, y, width, height);
            fillers.push(new HoleArray(x, y, width, height, cx, heights, radius, 8));
            discs.push(new DiscArray(cx, y, height, heights, radius, 8));
        }
    } else {
        // The plain front edge when there's no hutch
        const
            x = model.depth - HOLE_ARRAY_WIDTH,
            y = Math.max(offset, model.bottomFrontRadius),
            width = HOLE_ARRAY_WIDTH - offset,
            height = model.height - y - (model.topFrontCutbackHeight || offset),
            cx = model.depth - HOLE_SETBACK,
            radius = HOLE_RADIUS;
        grid.void(x, y, width, height);
        fillers.push(new HoleArray(x, y, width, height, cx, heights, radius, 8));
        discs.push(new DiscArray(cx, y, height, heights, radius, 8));
    }
}

const generateHeights = (height: number) => {
    const
        top = height - 0.375,
        // inc is the height of the span between holes
        inc = 32/25.4,
        // count is the number of holes; one more than the number of spans
        count = 1 + Math.floor(top / inc),
        // the distance from top to bottom is the number of spans times inc,
        // so the bottom is located this distance below the top
        bottom = top - ((count - 1) * inc),
        result = new Array(count);

    for(let i = 0; i < count; i++) {
        result[i] = bottom + i*inc;
    }
    return result;
};

// Little test of generateHeights for visual inspection
//for(let i = 1; i < 50; i += 3) {
//    console.log(i, generateHeights(i));
//}
